<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { configuredOauthClients } from '../lib/oauth-clients.js'
import { useSignupReward } from '../stores/signup-reward.js'
import EmailAuthForm from './auth/EmailAuthForm.vue'
import OauthButtons from './auth/OauthButtons.vue'
import VgIcon from './trpg/icons/VgIcon.vue'

// 登录和注册是同一个入口：第三方一键进入，或邮箱收验证码进入；没有密码
const props = defineProps({
  open: { type: Boolean, required: true },
  account: { type: Object, required: true },
  initialEmail: { type: String, default: '' },
  pageMode: { type: Boolean, default: false },
  returnTo: { type: String, default: '/account' },
})
const emit = defineEmits(['update:open', 'signed-in'])
const { t } = useI18n()
const signupReward = useSignupReward()
const hasOauthClients = configuredOauthClients().length > 0
// 'choose'：第一屏只列登录方式；'email'：邮箱验证码表单。没配任何平台时直接进 'email'
const step = ref(hasOauthClients ? 'choose' : 'email')
const localError = ref('')

watch(() => props.open, open => {
  if (!open) return
  step.value = hasOauthClients ? 'choose' : 'email'
  localError.value = ''
}, { immediate: true })

function close() {
  emit('update:open', false)
  localError.value = ''
}

function handleSignedIn() {
  emit('signed-in')
  close()
}

// 授权弹窗已把 token 写进 localStorage，这里只需把账号拉回来
async function handlePopupSignedIn() {
  await props.account.load()
  handleSignedIn()
}
</script>

<template>
  <Teleport to="body" :disabled="pageMode">
    <div v-if="open" class="start-auth-dialog" :class="{ 'is-page': pageMode }" :role="pageMode ? undefined : 'dialog'" :aria-modal="pageMode ? undefined : 'true'" :aria-label="t('visualGame.account.signIn')">
      <button v-if="!pageMode" class="start-auth-scrim" type="button" :aria-label="t('visualGame.actions.cancel')" @click="close"></button>
      <section class="start-auth-sheet">
      <button v-if="!pageMode" class="start-auth-close" type="button" :aria-label="t('visualGame.actions.cancel')" @click="close">×</button>
      <header class="start-auth-header">
        <component :is="pageMode ? 'a' : 'div'" class="start-auth-brand" :href="pageMode ? '/' : undefined" :aria-label="pageMode ? t('visualGame.landing.homeLabel') : undefined">
          <img src="/assets/brand/voko-logo.webp" alt="">
          <span>voko<em>The Playable Drama</em></span>
        </component>
        <button v-if="step === 'email' && hasOauthClients" class="start-auth-method-back" type="button" :aria-label="t('visualGame.actions.cancel')" @click="step = 'choose'">
          <VgIcon name="back" :size="20" />
        </button>
        <h2>{{ t('auth.social.loginOrSignUp') }}</h2>
        <p class="start-auth-pitch">{{ t('visualGame.authDialog.pitch') }}</p>
        <p v-if="signupReward.points > 0" class="start-auth-reward" data-testid="auth-signup-reward">
          <VgIcon name="coin" :size="16" />
          <span>{{ t('visualGame.authDialog.signupReward', { points: signupReward.points }) }}</span>
        </p>
      </header>
      <p v-if="localError" class="account-error">{{ localError }}</p>
      <OauthButtons v-if="step === 'choose'" :account="account" :return-to="returnTo" @error="msg => localError = msg" @signed-in="handlePopupSignedIn" />
      <EmailAuthForm v-else :account="account" :initial-email="initialEmail" @signed-in="handleSignedIn" />
      <div v-if="step === 'choose'" class="start-auth-switch">
        <button type="button" @click="step = 'email'">{{ t('auth.social.email') }}</button>
      </div>
      <p v-if="pageMode" class="start-auth-mobile-legal">
        {{ t('auth.legal.agree') }}
        <a href="/legal/terms">{{ t('auth.legal.terms') }}</a>
        {{ t('auth.legal.and') }}
        <a href="/legal/privacy">{{ t('auth.legal.privacy') }}</a>
      </p>
      </section>
    </div>
  </Teleport>
</template>
